// ---------------------------------------------------------------------------
// schedule.ts — which playlist should be playing right now?
// ---------------------------------------------------------------------------
//
// Deliberately pure: no `Date`, no `Intl`, no I/O. The caller passes the clock
// in. That is what makes "is the 22:00 window active at 00:30 on Sunday?"
// answerable in a test instead of only at 00:30 on a Sunday.
//
// Reading the clock is the caller's job because the answer depends on the
// PROPERTY's timezone, not the browser's — a manager working from another
// country must not shift the hotel's music. See `propertyClock()` in
// ./property-clock.ts.
//
// Two representations, chosen for comparison rather than display:
//
//   minutes from midnight   0..1439. Integers compare with `<`; 'HH:MM' strings
//                           need parsing at every comparison.
//   weekday bitmask         Sunday = bit 0 .. Saturday = bit 6. One integer
//                           column instead of a join table, and "is this day
//                           included" is a single AND.
// ---------------------------------------------------------------------------

export const MINUTES_PER_DAY = 1440;
export const DAYS_PER_WEEK = 7;

/** A schedule row, reduced to what the decision needs. */
export interface ScheduleWindow {
  id: string;
  playlistId: string;
  /** Sunday = bit 0 … Saturday = bit 6. */
  daysMask: number;
  /** Inclusive, 0..1439. */
  startMinute: number;
  /** Exclusive, 1..1440. Less than `startMinute` means the window wraps midnight. */
  endMinute: number;
  /** Higher wins when two windows overlap. */
  priority: number;
  isActive: boolean;
}

/** Is `weekday` (0 = Sunday) set in `mask`? */
export function dayInMask(mask: number, weekday: number): boolean {
  return (mask & (1 << weekday)) !== 0;
}

/** The day before `weekday`, wrapping Sunday back to Saturday. */
function previousDay(weekday: number): number {
  return (weekday + DAYS_PER_WEEK - 1) % DAYS_PER_WEEK;
}

/** Does this window wrap past midnight (22:00 → 02:00)? */
export function wrapsMidnight(w: Pick<ScheduleWindow, 'startMinute' | 'endMinute'>): boolean {
  return w.endMinute < w.startMinute;
}

/**
 * Is one window active at this instant?
 *
 * The wrapped case is the one worth spelling out. A window of 22:00 → 02:00 set
 * for Saturday covers Saturday 23:00 **and Sunday 00:30** — the tail after
 * midnight belongs to the day the window STARTED on, so Sunday 00:30 is matched
 * by checking whether *Saturday* is in the mask. Attributing it to Sunday
 * instead would play the wrong music on both nights: silence on Saturday night
 * and an unexpected window in the early hours of Sunday.
 */
export function isWindowActiveAt(
  w: ScheduleWindow,
  minuteOfDay: number,
  weekday: number,
): boolean {
  if (!w.isActive) return false;

  // A zero-length window can never be active. The database CHECK forbids
  // start == end, so this only guards against a caller constructing one by
  // hand; treating it as "always active" would be a surprising reading.
  if (w.startMinute === w.endMinute) return false;

  if (!wrapsMidnight(w)) {
    return (
      dayInMask(w.daysMask, weekday) &&
      minuteOfDay >= w.startMinute &&
      minuteOfDay < w.endMinute
    );
  }

  const startedToday = dayInMask(w.daysMask, weekday) && minuteOfDay >= w.startMinute;
  const startedYesterday =
    dayInMask(w.daysMask, previousDay(weekday)) && minuteOfDay < w.endMinute;

  return startedToday || startedYesterday;
}

/**
 * Order two overlapping windows. Lower result sorts first (wins).
 *
 * Total and deterministic — it never returns 0 for two different rows, so the
 * winner cannot depend on the order the database happened to return. A
 * non-deterministic tiebreak here would show up as music that changes when
 * nothing changed, which is close to impossible to diagnose from a report.
 */
function compareWindows(a: ScheduleWindow, b: ScheduleWindow): number {
  if (a.priority !== b.priority) return b.priority - a.priority;
  // Later start wins: a narrow window set inside a broad one is the more
  // specific intent ("09:00-11:00 jazz" inside "07:00-12:00 ambient").
  if (a.startMinute !== b.startMinute) return b.startMinute - a.startMinute;
  return a.id < b.id ? -1 : 1;
}

/**
 * The window that should be playing, or null.
 *
 * @param minuteOfDay 0..1439 in the property's timezone
 * @param weekday     0 = Sunday, in the property's timezone
 */
export function resolveActiveSchedule(
  windows: readonly ScheduleWindow[],
  minuteOfDay: number,
  weekday: number,
): ScheduleWindow | null {
  const active = windows.filter((w) => isWindowActiveAt(w, minuteOfDay, weekday));
  if (active.length === 0) return null;
  // Copy before sorting: callers pass query results they may still be using.
  return [...active].sort(compareWindows)[0];
}

/**
 * Minutes until the resolved window would change, or null if it never does.
 *
 * Used to arm a single wake-up at the next boundary instead of polling. It walks
 * forward a minute at a time rather than computing candidate boundaries
 * analytically — at most 10,080 evaluations of a filter over a handful of rows,
 * which costs microseconds and happens once per boundary, not per frame. The
 * analytic version has to re-derive the wrap-and-weekday reasoning above and
 * gets it subtly wrong; this cannot disagree with `resolveActiveSchedule`
 * because it asks it.
 *
 * Returns null when nothing changes within the horizon — the caller then arms
 * nothing and relies on `visibilitychange`, which is correct: there is no
 * boundary to wake for.
 */
export function minutesUntilNextChange(
  windows: readonly ScheduleWindow[],
  minuteOfDay: number,
  weekday: number,
  horizonMinutes: number = MINUTES_PER_DAY * DAYS_PER_WEEK,
): number | null {
  const currentId = resolveActiveSchedule(windows, minuteOfDay, weekday)?.id ?? null;

  for (let delta = 1; delta <= horizonMinutes; delta++) {
    const absolute = minuteOfDay + delta;
    const futureMinute = absolute % MINUTES_PER_DAY;
    const futureWeekday =
      (weekday + Math.floor(absolute / MINUTES_PER_DAY)) % DAYS_PER_WEEK;

    const nextId = resolveActiveSchedule(windows, futureMinute, futureWeekday)?.id ?? null;
    if (nextId !== currentId) return delta;
  }

  return null;
}

// ---------------------------------------------------------------------------
// Conversions for the editor UI
// ---------------------------------------------------------------------------

/** '07:30' → 450. Returns null on anything that is not a valid time of day. */
export function parseTimeToMinutes(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/**
 * 450 → '07:30'. Accepts 1440 as '24:00' so an end-of-day window round-trips
 * through the editor instead of collapsing to '00:00' and inverting the window.
 */
export function formatMinutesAsTime(minute: number): string {
  const clamped = Math.max(0, Math.min(MINUTES_PER_DAY, Math.trunc(minute)));
  const hours = Math.floor(clamped / 60);
  const minutes = clamped % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** [0, 6] → 0b1000001 (Sunday + Saturday). */
export function daysToMask(days: readonly number[]): number {
  return days.reduce((mask, d) => (d >= 0 && d < DAYS_PER_WEEK ? mask | (1 << d) : mask), 0);
}

/** 0b1000001 → [0, 6]. */
export function maskToDays(mask: number): number[] {
  const out: number[] = [];
  for (let d = 0; d < DAYS_PER_WEEK; d++) if (dayInMask(mask, d)) out.push(d);
  return out;
}
